// Port of ios/Sources/ViewModels/ChannelLinks.swift.
//
// `~channel` links, both halves (docs/PARITY.md "Channel-link autocomplete"): the composer's
// autocomplete while one is being typed, and finding the links in already-rendered message
// text so the view layer can colour them and open the channel on tap.
//
// The wire is the literal text: the desktop inserts `~slug` and renders it as a link by
// looking the slug up in its own channel store — there is no server contract here at all.
// Only KNOWN slugs highlight, for the same reason only known usernames do in
// `MentionHighlight`.
//
// WEB OFFSETS: every offset is a UTF-16 code unit (Swift: Characters). The composer half
// (`active`/`apply`) scans code units, like mentionComposer.ts — a `<textarea>`'s
// `selectionStart` speaks UTF-16 and every character the trigger rule looks at (`~`, ASCII
// slug characters, whitespace) is one unit. The rendered-text half (`spans`) scans Swift
// Characters (swiftCharacters.ts) and reports UTF-16 offsets, like `MentionHighlight`.

import { characterView, isASCII, isASCIILowercase, isLetter, isNumber, isWhitespace } from './swiftCharacters';

export interface ChannelLinkTarget {
  channelId: string;
  /** The URL slug (`ChannelRecord.name`) — what `~` completion inserts. */
  slug: string;
  displayName: string;
  isPrivate: boolean;
}

/** A "~" the user is typing at: `start` is the "~" itself, `query` what follows it. */
export interface ChannelLinkActive {
  start: number;
  query: string;
}

function isLinkCharacter(c: string): boolean {
  return (isASCII(c) && (isLetter(c) || isNumber(c))) || c === '_' || c === '-';
}

/**
 * Same boundary rule as a mention: the "~" only triggers at the start of the text or
 * after whitespace, so `casa~media` and a path fragment never open the strip; and the
 * cursor must sit inside or at the end of the token.
 */
function active(text: string, cursor: number): ChannelLinkActive | null {
  if (cursor < 1 || cursor > text.length) return null;
  let index = cursor - 1;
  while (index >= 0 && isLinkCharacter(text[index])) index -= 1;
  if (index < 0 || text[index] !== '~') return null;
  if (index > 0 && !isWhitespace(text[index - 1])) return null;
  return { start: index, query: text.slice(index + 1, cursor) };
}

/**
 * Foundation `folding(options: [.diacriticInsensitive, .caseInsensitive])`: decompose, drop
 * the combining marks, lower-case. (Recomposed so Hangul and friends compare as typed.)
 */
function folded(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').normalize('NFC').toLowerCase();
}

/**
 * Candidates for `query`, alphabetical by display name, capped. Matching is contains on
 * the slug and accent-folded contains on the display name.
 */
function filter(targets: readonly ChannelLinkTarget[], query: string, limit = 6): ChannelLinkTarget[] {
  const needle = folded(query);
  return targets
    .filter((t) => needle === '' || t.slug.includes(needle) || folded(t.displayName).includes(needle))
    .map((t) => ({ t, key: folded(t.displayName) }))
    .sort((a, b) => {
      // Swift's `<` on Strings; JS `<` compares UTF-16 units — identical outside astral text.
      if (a.key !== b.key) return a.key < b.key ? -1 : 1;
      return a.t.slug < b.t.slug ? -1 : a.t.slug > b.t.slug ? 1 : 0;
    })
    .slice(0, limit)
    .map(({ t }) => t);
}

export interface ChannelLinkApplied {
  text: string;
  cursor: number;
}

/** Replaces the active token with `~slug ` (trailing space, as mentions do). */
function apply(args: { text: string; cursor: number; active: ChannelLinkActive; slug: string }): ChannelLinkApplied {
  const { text, cursor, active, slug } = args;
  const replacement = '~' + slug + ' ';
  const newText = text.slice(0, active.start) + replacement + text.slice(cursor);
  return { text: newText, cursor: active.start + replacement.length };
}

export interface ChannelLinkSpan {
  start: number;
  end: number;
  slug: string;
}

function isSlugCharacter(c: string): boolean {
  return (isASCII(c) && (isASCIILowercase(c) || isNumber(c))) || c === '_' || c === '-';
}

/** Every `~slug` of a KNOWN channel in rendered text, with the mention boundary rule. */
function spans(text: string, knownSlugs: ReadonlySet<string>): ChannelLinkSpan[] {
  if (text === '' || knownSlugs.size === 0) return [];
  // WEB: cheap guard, same reasoning as MentionHighlight's "@" test — no "~", no link.
  if (!text.includes('~')) return [];
  const { chars, offsets } = characterView(text);
  const result: ChannelLinkSpan[] = [];
  let i = 0;
  while (i < chars.length) {
    if (chars[i] !== '~') {
      i += 1;
      continue;
    }
    if (i > 0 && !isWhitespace(chars[i - 1]) && chars[i - 1] !== '(') {
      i += 1;
      continue;
    }
    let j = i + 1;
    while (j < chars.length && isSlugCharacter(chars[j])) j += 1;
    if (!(j > i + 1)) {
      i += 1;
      continue;
    }
    const slug = chars.slice(i + 1, j).join('');
    if (knownSlugs.has(slug)) {
      result.push({ start: offsets[i], end: offsets[j], slug });
    }
    i = j;
  }
  return result;
}

export const ChannelLinks = {
  active,
  filter,
  apply,
  spans,
} as const;
